import Order from '../models/Order';
import { isCollabActive } from './orderCollabStorage';

export { isCollabActive, hasCollabPreference, isCollabSuspended } from './orderCollabStorage';

export const isOrderEditableLocally = (order?: Order | null): boolean => {
    if (!order || !order.orderheader) {
        return false;
    }

    const header = order.orderheader;
    return header.orderState === 0 && !header.orderIsFinal;
};

export const readOrderIdFromPath = (pathname: string) => {
    const dashboardMatch = pathname.match(/\/dashboard\/(\d+)/);
    if (dashboardMatch) {
        return +dashboardMatch[1];
    }

    const checkoutMatch = pathname.match(/\/checkout\/(\d+)/);
    if (checkoutMatch) {
        return +checkoutMatch[1];
    }

    return null;
};

export const isActiveOrderRoute = (pathname: string) =>
    pathname.indexOf('/shop') === 0 ||
    pathname.indexOf('/dashboard') === 0 ||
    pathname.indexOf('/checkout') === 0 ||
    pathname.indexOf('/search') === 0 ||
    pathname.indexOf('/edit') === 0;

export const isOrderHistoryPath = (pathname: string) =>
    pathname === '/activity-history' || pathname.startsWith('/activity-history/');

export const isMyOrdersPath = (pathname: string) =>
    pathname === '/my-orders' || pathname.startsWith('/my-orders/');

/** Shop shell pages where the current open order session should stay available. */
export const isShopUtilityPath = (pathname: string) =>
    isOrderHistoryPath(pathname) ||
    isMyOrdersPath(pathname) ||
    pathname === '/download';

export const supportsOrderActivityContext = (pathname: string) =>
    isActiveOrderRoute(pathname) || isShopUtilityPath(pathname);

export const resolveCurrentEditableOrderContext = (
    orderState: { order?: Order | null; dashboardOrder?: Order | null },
    pathname: string
): { orderId: number; order: Order } | null => {
    if (!supportsOrderActivityContext(pathname)) {
        return null;
    }

    const order = orderState.order || orderState.dashboardOrder;
    if (!isOrderEditableLocally(order) || !order || !order.orderheader) {
        return null;
    }

    const orderId = order.orderheader.appOrderId;
    const resolvedId = resolveActiveOrderIdFromState(orderState, pathname);

    if (!resolvedId || resolvedId !== orderId) {
        return null;
    }

    return { orderId, order };
};

export const resolveActiveOrderId = (pathname: string, fromOrder: number | null) => {
    if (fromOrder) {
        return fromOrder;
    }

    const fromPath = readOrderIdFromPath(pathname);
    if (fromPath) {
        return fromPath;
    }

    if (supportsOrderActivityContext(pathname)) {
        const storedOrderId = localStorage.getItem('order');
        if (storedOrderId) {
            return +storedOrderId;
        }
    }

    return null;
};

export const resolveActiveOrderIdFromState = (
    orderState: { order?: Order | null; dashboardOrder?: Order | null },
    pathname: string
): number | null => {
    const activeOrder = orderState.order || orderState.dashboardOrder;
    const fromOrder = activeOrder && activeOrder.orderheader
        ? activeOrder.orderheader.appOrderId
        : null;

    return resolveActiveOrderId(pathname, fromOrder);
};

export const isActiveOrderInOpenOrders = (
    orders: Array<{ appOrderId: number; orderState?: number }>,
    activeId: number
): boolean =>
    orders.some((entry) => entry.appOrderId === activeId && (entry.orderState === 0 || entry.orderState === undefined));

export const isOrderLoadedInState = (
    orderState: { order?: Order | null; dashboardOrder?: Order | null },
    orderId: number
): boolean => {
    if (
        orderState.order &&
        orderState.order.orderheader &&
        orderState.order.orderheader.appOrderId === orderId
    ) {
        return true;
    }

    if (
        orderState.dashboardOrder &&
        orderState.dashboardOrder.orderheader &&
        orderState.dashboardOrder.orderheader.appOrderId === orderId
    ) {
        return true;
    }

    return false;
};

export const clearStoredOrderId = () => {
    localStorage.removeItem('order');
};

export const readStoredOrderId = (): number | null => {
    const stored = localStorage.getItem('order');
    if (!stored) {
        return null;
    }

    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

export const clearStaleStoredOrderIfMissingFromOpenOrders = (
    orders: Array<{ appOrderId: number; orderState?: number }>,
    orderState: { order?: Order | null; dashboardOrder?: Order | null }
): void => {
    const storedId = readStoredOrderId();
    if (storedId == null) {
        return;
    }

    if (isActiveOrderInOpenOrders(orders, storedId)) {
        return;
    }

    if (isOrderLoadedInState(orderState, storedId)) {
        return;
    }

    clearStoredOrderId();
};

export const hasActiveOrderSession = (
    orderState: { order?: Order | null; dashboardOrder?: Order | null },
    pathname: string,
    activeId: number
): boolean => {
    if (orderState.order && orderState.order.orderheader && orderState.order.orderheader.appOrderId === activeId) {
        return true;
    }

    if (
        orderState.dashboardOrder &&
        orderState.dashboardOrder.orderheader &&
        orderState.dashboardOrder.orderheader.appOrderId === activeId
    ) {
        return true;
    }

    if (localStorage.getItem('order') === String(activeId)) {
        return true;
    }

    return readOrderIdFromPath(pathname) === activeId;
};

export const isOnDashboardPath = (pathname: string) => pathname.indexOf('/dashboard') !== -1;

export const getPathAfterClosingOrder = (pathname: string): string | null => {
    if (pathname.match(/\/dashboard\/\d+/)) {
        return '/dashboard';
    }

    if (pathname.match(/\/checkout\/\d+/)) {
        return '/shop';
    }

    if (pathname.match(/\/previeworder\/\d+/)) {
        return '/shop';
    }

    return null;
};

export const isAutoUpdateEnabled = (orderId: number | null, login?: string | null) =>
    isCollabActive(login, orderId);
